import React, { useEffect, useMemo } from 'react';

import { useAtom } from 'jotai';
import { Form } from 'semantic-ui-react';

import { useBaseline } from './hooks/useBaseline';
import { useOptimizationOptions, useOptimizationForm } from './hooks/useOptimization';
import { useBaselinePeriod, useBudgetPeriod } from './hooks/usePeriods';
import { SplitLayout, FormWrapSegment, FormDescription } from './parts';
import { RadioDropdown } from '../../../components/select/SelectRadioList';
import { formatCurrency, numberToTwoDecimals } from '../../../utils/valueFormatter';
import { optimizationAtom, budgetAtom, optionalTargetAtom, isDirtyAtom, invalidFormAtom } from '../atoms';
import { OPTIMIZATION_OPTIONS } from '../constants';

const Optimization = ({ isConversionKPI, baseline }) => {
  const [budget, setBudget] = useAtom(budgetAtom);
  const [optionalTarget, setOptionalTarget] = useAtom(optionalTargetAtom);
  const [optimization, setOptimization] = useAtom(optimizationAtom);
  const [, setInvalidForm] = useAtom(invalidFormAtom);
  const [, setIsDirty] = useAtom(isDirtyAtom);
  const { budgetPeriod, budgetPeriodName } = useBudgetPeriod();
  const { periodValue: baselinePeriod } = useBaselinePeriod();
  const { baselineTotal, baselineRevenueTotal } = useBaseline({ baseline });
  const { optimizationDescriptions, optimizationOptions, selectedOptimizationOption } = useOptimizationOptions(isConversionKPI, budgetPeriodName, optimization);
  const {
    localBudget, setLocalBudget, resetBudget,
    localOptionalTarget, setLocalOptionalTarget, resetOptionalTarget,
    validateBudget, validateOptionalTarget,
  } = useOptimizationForm();

  const isDollarIcon = useMemo(() => (
    optimization !== OPTIMIZATION_OPTIONS.targetMetric || (optimization === OPTIMIZATION_OPTIONS.targetMetric && isConversionKPI)
  ), [optimization, isConversionKPI]);

  // Break down baseline calculations
  const baselineRoas = baselineRevenueTotal / baselineTotal;
  const baselineMetric = isConversionKPI ? 1 / baselineRoas : baselineRoas;
  const baselineMetricText = isConversionKPI ? formatCurrency(baselineMetric, 'USD', 2) : numberToTwoDecimals(baselineMetric);
  const baselineSpend = formatCurrency(baselineTotal * budgetPeriod, 'USD', 0);

  const optionalTargetPlaceholder = useMemo(() => {
    if (!selectedOptimizationOption.inputOptionalPlaceholder) {
      return 'Optional';
    }

    const isTargetMetric = optimization === OPTIMIZATION_OPTIONS.targetMetric;
    return isTargetMetric
      ? selectedOptimizationOption.inputOptionalPlaceholder(baselineSpend)
      : selectedOptimizationOption.inputOptionalPlaceholder(baselineMetricText, isConversionKPI);
  }, [selectedOptimizationOption, optimization, baselineSpend, baselineMetricText, isConversionKPI]);

  // Validation
  const optionalTargetError = validateOptionalTarget(optimization, baselineRoas, localOptionalTarget, isConversionKPI);
  const budgetError = validateBudget(optimization, baselineTotal, budgetPeriod, localBudget);

  const onBudgetChange = (e, { value }) => {
    setLocalBudget(value);
    setIsDirty(true);
  };

  const onOptionalTargetChange = (e, { value }) => {
    setLocalOptionalTarget(value);
    setIsDirty(true);
  };

  useEffect(() => {
    if (optimization) {
      setOptimization(false);
      resetBudget(optimization, baselineTotal, budgetPeriod, baselineMetric);
      resetOptionalTarget();
      setIsDirty(true);
    }
  }, [budgetPeriod, baselinePeriod]);

  useEffect(() => {
    if (!budgetError && localBudget !== budget) {
      if (optimization === OPTIMIZATION_OPTIONS.targetMetric) {
        setBudget(isConversionKPI ? 1 / localBudget : localBudget);
      } else {
        setBudget(localBudget / budgetPeriod);
      }
    }
  }, [localBudget]);

  useEffect(() => {
    if (!optionalTargetError && localOptionalTarget !== optionalTarget) {
      if (!localOptionalTarget || localOptionalTarget === '') {
        setOptionalTarget(false);
      } else if (optimization === OPTIMIZATION_OPTIONS.targetMetric) {
        setOptionalTarget(localOptionalTarget / budgetPeriod);
      } else {
        setOptionalTarget(isConversionKPI ? 1 / localOptionalTarget : localOptionalTarget);
      }
    }
  }, [localOptionalTarget]);

  useEffect(() => {
    if (budgetError || optionalTargetError) {
      setInvalidForm(true);
    } else {
      setInvalidForm(false);
    }
  }, [budgetError, optionalTargetError]);

  return (
    <SplitLayout
      header="4. Choose Scenario"
      leftContent={(
        <FormWrapSegment key={baselineTotal + optimization}>
          <Form>
            <RadioDropdown
              defaultValue={optimization}
              onChange={(e, { value }) => {
                setIsDirty(true);
                setOptimization(value);
                resetBudget(value, baselineTotal, budgetPeriod, baselineMetric);
                resetOptionalTarget();
              }}
              options={optimizationOptions}
            />
            {optimization && (
              <>
                <Form.Input
                  label={selectedOptimizationOption.inputLabel}
                  placeholder={selectedOptimizationOption.inputPlaceholder}
                  value={!localBudget ? '' : localBudget}
                  onChange={onBudgetChange}
                  icon={isDollarIcon ? 'dollar' : false}
                  iconPosition={isDollarIcon ? 'left' : false}
                  error={budgetError && {
                    content: budgetError,
                    pointing: 'above',
                  }}
                />
                <Form.Input
                  label={selectedOptimizationOption.inputOptionalLabel}
                  placeholder={optionalTargetPlaceholder}
                  value={!localOptionalTarget ? '' : localOptionalTarget}
                  onChange={onOptionalTargetChange}
                  icon={isConversionKPI ? 'dollar' : false}
                  iconPosition={isConversionKPI ? 'left' : false}
                  error={optionalTargetError && {
                    content: optionalTargetError,
                    pointing: 'above',
                  }}
                />
              </>
            )}
          </Form>
        </FormWrapSegment>
      )}
      rightContent={<FormDescription>{optimizationDescriptions[optimization] || optimizationDescriptions.default}</FormDescription>}
    />
  );
};

export default Optimization;
